import { Client } from "@stomp/stompjs";

import { WebSocketConfig } from "./websocket";

const ConnectStatusEnum = {
  UNCONNECTED: "UNCONNECTED",
  CONNECTED: "CONNECTED",
  ERROR: "ERROR",
};

/** 一个简化websocket初始化配置的工具库 */
export default class StompClient {
  /** 工具实例 */
  #client;

  /** socket连接状态 */
  #connectStatus;

  /** 记录已订阅的请求 */
  #subscriptionMap;

  /** socket唯一实例 */
  static #instance;

  constructor() {
    this.#connectStatus = ConnectStatusEnum.UNCONNECTED;
    this.#client = new Client({
      brokerURL: WebSocketConfig.WS_URL,
      connectHeaders: {
        login: WebSocketConfig.WS_LOGIN,
        passcode: WebSocketConfig.WS_PASS_CODE,
        host: WebSocketConfig.WS_HOST,
      },
      onDisconnect: () => {
        this.#connectStatus = ConnectStatusEnum.UNCONNECTED;
      },
      onWebSocketClose: () => {
        this.#connectStatus = ConnectStatusEnum.UNCONNECTED;
      },
    });
    this.#subscriptionMap = new Map();
  }

  static getInstance() {
    if (!StompClient.#instance) {
      StompClient.#instance = new StompClient();
    }
    return StompClient.#instance;
  }

  /**
   * 开启监听某主题
   * @param topic string
   * @returns Promise result.data
   */
  subscribeTopic(topic) {
    return new Promise((resolve, reject) => {
      if (this.#connectStatus === ConnectStatusEnum.ERROR) {
        reject(new Error("渲染服务异常"));
        return;
      }
      const subscribeHandler = () => {
        const subscription = this.#client.subscribe(
          WebSocketConfig.WS_TOPIC(topic),
          (message) => {
            const result = JSON.parse(message.body);
            if (result.progress === 100) {
              if (result.error_msg) {
                reject(new Error(result.error_msg));
              } else {
                resolve(result.data);
              }
              if (this.isTopicExist(topic)) {
                this.#deleteTopic(topic);
              }
            }
          }
        );
        this.#subscriptionMap.set(topic, subscription);
      };
      if (this.#connectStatus === ConnectStatusEnum.CONNECTED) {
        subscribeHandler();
        return;
      }
      // 走到这里，说明未建立连接
      this.#client.onConnect = () => {
        this.#connectStatus = ConnectStatusEnum.CONNECTED;
        subscribeHandler();
      };
      this.#client.onStompError = () => {
        this.#connectStatus = ConnectStatusEnum.ERROR;
        this.#client.deactivate();
        // eslint-disable-next-line no-console
        console.error(
          "The RabbitMQ queue server connect error, It shouldn't happen, the possible cause is the login,  passcode and host don't match"
        );
        reject(new Error("渲染服务异常"));
      };
      this.#client.activate();
    });
  }

  /**
   * 处理中任务数量
   */
  size() {
    return this.#subscriptionMap.size;
  }

  cancelTopic(topic) {
    this.#deleteTopic(topic);
  }

  isTopicExist(topic) {
    return this.#subscriptionMap.has(topic);
  }

  /**
   * 当前列表为空时，断开连接
   */
  #deactivateIfEmpty() {
    const size = this.size();
    if (size === 0) {
      this.#client.deactivate();
    }
  }

  // eslint-disable-next-line no-dupe-class-members
  #deleteTopic(topic) {
    const subscription = this.#subscriptionMap.get(topic);
    if (subscription) {
      subscription.unsubscribe();
      this.#subscriptionMap.delete(topic);
      this.#deactivateIfEmpty();
    }
  }
}
